import { create } from 'zustand';
import { createDemoState, type DemoState } from '@/demo/demoData';

const STORAGE_KEY = 'ludolio.librarian.demo';

interface PersistedShape {
  isDemoMode: boolean;
  data: DemoState;
}

interface DemoStoreState {
  isDemoMode: boolean;
  data: DemoState | null;
  enterDemo: () => DemoState;
  exitDemo: () => void;
  updateData: (mutator: (draft: DemoState) => void) => void;
}

function readSession(): PersistedShape | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedShape;
  } catch {
    return null;
  }
}

function writeSession(state: PersistedShape | null) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (state === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage can throw in private mode — ignore, demo still works for the current tab life.
  }
}

const initial = readSession();

export const useDemoStore = create<DemoStoreState>((set, get) => ({
  isDemoMode: initial?.isDemoMode ?? false,
  data: initial?.data ?? null,

  enterDemo: () => {
    const fresh = createDemoState();
    set({ isDemoMode: true, data: fresh });
    writeSession({ isDemoMode: true, data: fresh });
    return fresh;
  },

  exitDemo: () => {
    set({ isDemoMode: false, data: null });
    writeSession(null);
  },

  updateData: (mutator) => {
    const current = get().data;
    if (!current) return;
    // Mutate a structural clone so React sees a new reference.
    const next = JSON.parse(JSON.stringify(current)) as DemoState;
    mutator(next);
    set({ data: next });
    writeSession({ isDemoMode: true, data: next });
  },
}));
